// ============================================================
// src/components/common/index.jsx
// All reusable primitive components
// ============================================================

import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';
import styles from './common.module.css';

// ── BUTTON ──────────────────────────────────────────────────
export const Button = ({
  children, variant = 'primary', size = 'md',
  loading = false, disabled = false,
  icon, iconRight, onClick, type = 'button',
  fullWidth = false, className, ...props
}) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled || loading}
    className={clsx(
      styles.btn,
      styles[`btn-${variant}`],
      styles[`btn-${size}`],
      fullWidth && styles['btn-full'],
      loading && styles['btn-loading'],
      className
    )}
    {...props}
  >
    {loading ? (
      <span className={styles.spinner} />
    ) : (
      <>
        {icon && <span className={styles['btn-icon']}>{icon}</span>}
        {children}
        {iconRight && <span className={styles['btn-icon-right']}>{iconRight}</span>}
      </>
    )}
  </button>
);

// ── BADGE ────────────────────────────────────────────────────
export const Badge = ({ children, variant = 'gray', dot = true, className }) => (
  <span className={clsx(styles.badge, styles[`badge-${variant}`], className)}>
    {dot && <span className={styles['badge-dot']} />}
    {children}
  </span>
);

// ── CARD ─────────────────────────────────────────────────────
export const Card = ({ children, className, hover = false, padding = true }) => (
  <div className={clsx(styles.card, hover && styles['card-hover'], !padding && styles['card-no-pad'], className)}>
    {children}
  </div>
);

export const CardHeader = ({ title, subtitle, actions, className }) => (
  <div className={clsx(styles['card-header'], className)}>
    <div>
      <div className={styles['card-title']}>{title}</div>
      {subtitle && <div className={styles['card-subtitle']}>{subtitle}</div>}
    </div>
    {actions && <div className={styles['card-actions']}>{actions}</div>}
  </div>
);

export const CardBody = ({ children, noPadding = false, className }) => (
  <div className={clsx(styles['card-body'], noPadding && styles['card-body-np'], className)}>
    {children}
  </div>
);

// ── STAT CARD ────────────────────────────────────────────────
export const StatCard = ({
  icon, value, label, change, changeType = 'up',
  accent = 'teal', loading = false, className,
}) => (
  <div className={clsx(styles['stat-card'], styles[`stat-${accent}`], className)}>
    <div className={clsx(styles['stat-icon'], styles[`stat-icon-${accent}`])}>
      {icon}
    </div>
    {loading ? (
      <div className={styles['stat-skeleton']} />
    ) : (
      <>
        <div className={styles['stat-value']}>{value}</div>
        <div className={styles['stat-label']}>{label}</div>
        {change && (
          <span className={clsx(styles['stat-change'], styles[`change-${changeType}`])}>
            {changeType === 'up' ? '↑' : '↓'} {change}
          </span>
        )}
      </>
    )}
  </div>
);

// ── INPUT ────────────────────────────────────────────────────
export const Input = ({
  label, error, hint, icon, type = 'text',
  id, required, className, ...props
}) => (
  <div className={clsx(styles['form-group'], className)}>
    {label && (
      <label htmlFor={id} className={styles['form-label']}>
        {label} {required && <span className={styles['required']}>*</span>}
      </label>
    )}
    <div className={styles['input-wrap']}>
      {icon && <span className={styles['input-icon']}>{icon}</span>}
      <input
        id={id}
        type={type}
        className={clsx(
          styles['form-input'],
          icon && styles['input-with-icon'],
          error && styles['input-error']
        )}
        {...props}
      />
    </div>
    {error && <span className={styles['form-error']}>{error}</span>}
    {hint && !error && <span className={styles['form-hint']}>{hint}</span>}
  </div>
);

// ── SELECT ───────────────────────────────────────────────────
export const Select = ({ label, error, id, children, className, required, ...props }) => (
  <div className={clsx(styles['form-group'], className)}>
    {label && (
      <label htmlFor={id} className={styles['form-label']}>
        {label} {required && <span className={styles['required']}>*</span>}
      </label>
    )}
    <select
      id={id}
      className={clsx(styles['form-input'], styles['form-select'], error && styles['input-error'])}
      {...props}
    >
      {children}
    </select>
    {error && <span className={styles['form-error']}>{error}</span>}
  </div>
);

// ── TEXTAREA ─────────────────────────────────────────────────
export const Textarea = ({ label, error, id, rows = 4, className, required, ...props }) => (
  <div className={clsx(styles['form-group'], className)}>
    {label && (
      <label htmlFor={id} className={styles['form-label']}>
        {label} {required && <span className={styles['required']}>*</span>}
      </label>
    )}
    <textarea
      id={id}
      rows={rows}
      className={clsx(styles['form-input'], styles['form-textarea'], error && styles['input-error'])}
      {...props}
    />
    {error && <span className={styles['form-error']}>{error}</span>}
  </div>
);

// ── MODAL ────────────────────────────────────────────────────
export const Modal = ({ open, onClose, title, children, size = 'md' }) => {
  const overlayRef = useRef();

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      className={styles['modal-overlay']}
      onClick={(e) => { if (e.target === overlayRef.current) onClose?.(); }}
    >
      <div className={clsx(styles.modal, styles[`modal-${size}`])}>
        {(title || onClose) && (
          <div className={styles['modal-header']}>
            {title && <h2 className={styles['modal-title']}>{title}</h2>}
            {onClose && (
              <button onClick={onClose} className={styles['modal-close']}>✕</button>
            )}
          </div>
        )}
        <div className={styles['modal-body']}>{children}</div>
      </div>
    </div>
  );
};

// ── TABLE ────────────────────────────────────────────────────
export const Table = ({ columns, data, loading, emptyMessage = 'No data found', onRowClick }) => {
  if (loading) return (
    <div className={styles['table-loading']}>
      {[...Array(5)].map((_, i) => (
        <div key={i} className={styles['skeleton-row']} style={{ opacity: 1 - i * 0.15 }} />
      ))}
    </div>
  );

  if (!data?.length) return (
    <div className={styles['table-empty']}>
      <div className={styles['empty-icon']}>📭</div>
      <div className={styles['empty-text']}>{emptyMessage}</div>
    </div>
  );

  return (
    <div className={styles['table-wrap']}>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} style={col.width ? { width: col.width } : {}}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr
              key={row.id ?? i}
              onClick={() => onRowClick?.(row)}
              className={onRowClick ? styles['row-clickable'] : ''}
            >
              {columns.map((col) => (
                <td key={col.key}>
                  {col.render ? col.render(row[col.key], row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── AVATAR ───────────────────────────────────────────────────
export const Avatar = ({ name = '', size = 'md', color = 'teal', src }) => {
  const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className={clsx(styles.avatar, styles[`avatar-${size}`], styles[`avatar-${color}`])}>
      {src ? <img src={src} alt={name} /> : initials}
    </div>
  );
};

// ── PROGRESS BAR ─────────────────────────────────────────────
export const ProgressBar = ({ value, max = 100, color = 'teal', showLabel = false }) => {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div className={styles['progress-wrap']}>
      <div className={styles['progress-bar']}>
        <div
          className={clsx(styles['progress-fill'], styles[`progress-${color}`])}
          style={{ width: `${pct}%` }}
        />
      </div>
      {showLabel && <span className={styles['progress-label']}>{Math.round(pct)}%</span>}
    </div>
  );
};

// ── ALERT ────────────────────────────────────────────────────
export const Alert = ({ children, variant = 'info', onClose }) => (
  <div className={clsx(styles.alert, styles[`alert-${variant}`])}>
    <span className={styles['alert-content']}>{children}</span>
    {onClose && <button onClick={onClose} className={styles['alert-close']}>✕</button>}
  </div>
);

// ── TABS ─────────────────────────────────────────────────────
export const Tabs = ({ tabs, active, onChange }) => (
  <div className={styles.tabs}>
    {tabs.map((tab) => (
      <button
        key={tab.value}
        className={clsx(styles.tab, active === tab.value && styles['tab-active'])}
        onClick={() => onChange(tab.value)}
      >
        {tab.label}
        {tab.count !== undefined && (
          <span className={styles['tab-count']}>{tab.count}</span>
        )}
      </button>
    ))}
  </div>
);

// ── SEARCH INPUT ─────────────────────────────────────────────
export const SearchInput = ({ value, onChange, placeholder = 'Search...', className }) => (
  <div className={clsx(styles['search-wrap'], className)}>
    <span className={styles['search-icon']}>🔍</span>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={styles['search-input']}
    />
  </div>
);

// ── EMPTY STATE ──────────────────────────────────────────────
export const EmptyState = ({ icon = '📭', title, description, action }) => (
  <div className={styles['empty-state']}>
    <div className={styles['empty-state-icon']}>{icon}</div>
    <h3 className={styles['empty-state-title']}>{title}</h3>
    {description && <p className={styles['empty-state-desc']}>{description}</p>}
    {action && <div className={styles['empty-state-action']}>{action}</div>}
  </div>
);

// ── LOADING SPINNER ──────────────────────────────────────────
export const Spinner = ({ size = 'md', className }) => (
  <div className={clsx(styles.spinner, styles[`spinner-${size}`], className)} />
);

// ── PAGE LOADER ──────────────────────────────────────────────
export const PageLoader = () => (
  <div className={styles['page-loader']}>
    <div className={styles['loader-logo']}>📚</div>
    <div className={styles['loader-text']}>Loading SolvIt...</div>
    <div className={styles.spinner} />
  </div>
);
